/**
 * API client — Comunicados de Plataforma (PlatformBroadcast)
 *
 * Wrappers tipados sobre authFetch. As rotas admin vivem em
 * /api/internal/platform-broadcasts (requireSuperAdmin) e as rotas do usuário
 * final em /api/platform-broadcasts (requireAuth).
 */

import { authFetch } from '@/utils/api';

const ADMIN_BASE = '/api/internal/platform-broadcasts';
const USER_BASE = '/api/platform-broadcasts';

async function parse(res) {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err = new Error(data.error || data.message || 'Erro na requisição');
        err.status = res.status;
        err.data = data;
        throw err;
    }
    return data;
}

// ─── Admin ──────────────────────────────────────────────────────────────────

export async function listBroadcasts({ status, page = 1, pageSize = 20 } = {}) {
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    qs.set('page', String(page));
    qs.set('pageSize', String(pageSize));
    return parse(await authFetch(`${ADMIN_BASE}?${qs.toString()}`));
}

export async function getBroadcast(id) {
    return parse(await authFetch(`${ADMIN_BASE}/${id}`));
}

export async function createBroadcast(payload) {
    return parse(await authFetch(ADMIN_BASE, { method: 'POST', body: JSON.stringify(payload) }));
}

export async function updateBroadcast(id, payload) {
    return parse(await authFetch(`${ADMIN_BASE}/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }));
}

export async function dispatchBroadcast(id) {
    return parse(await authFetch(`${ADMIN_BASE}/${id}/dispatch`, { method: 'POST', body: '{}' }));
}

export async function scheduleBroadcast(id, scheduledAt) {
    return parse(await authFetch(`${ADMIN_BASE}/${id}/schedule`, {
        method: 'POST',
        body: JSON.stringify({ scheduledAt }),
    }));
}

export async function cancelBroadcast(id) {
    return parse(await authFetch(`${ADMIN_BASE}/${id}/cancel`, { method: 'POST', body: '{}' }));
}

export async function deleteBroadcast(id) {
    return parse(await authFetch(`${ADMIN_BASE}/${id}`, { method: 'DELETE' }));
}

export async function previewAudience(audience) {
    return parse(await authFetch(`${ADMIN_BASE}/preview-audience`, {
        method: 'POST',
        body: JSON.stringify({ audience }),
    }));
}

export async function uploadMedia(file) {
    const form = new FormData();
    form.append('file', file);
    return parse(await authFetch(`${ADMIN_BASE}/upload-media`, { method: 'POST', body: form }));
}

export async function listWhatsappTemplates() {
    return parse(await authFetch(`${ADMIN_BASE}/whatsapp-templates`));
}

export async function getSettings() {
    return parse(await authFetch(`${ADMIN_BASE}/settings`));
}

export async function updateSettings(payload) {
    return parse(await authFetch(`${ADMIN_BASE}/settings`, { method: 'PATCH', body: JSON.stringify(payload) }));
}

// ─── Usuário final ────────────────────────────────────────────────────────────

export async function getPendingPopups() {
    return parse(await authFetch(`${USER_BASE}/pending`));
}

export async function getMyHistory({ page = 1, pageSize = 20 } = {}) {
    const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    return parse(await authFetch(`${USER_BASE}/history?${qs.toString()}`));
}

export async function getBroadcastViewer(id) {
    return parse(await authFetch(`${USER_BASE}/${id}`));
}

export async function markBroadcastSeen(id) {
    return parse(await authFetch(`${USER_BASE}/${id}/seen`, { method: 'POST', body: '{}' }));
}

export async function acknowledgeBroadcast(id, { scrolledToEnd = false, videoFinished = false } = {}) {
    return parse(await authFetch(`${USER_BASE}/${id}/acknowledge`, {
        method: 'POST',
        body: JSON.stringify({ scrolledToEnd, videoFinished }),
    }));
}

export default {
    listBroadcasts,
    getBroadcast,
    createBroadcast,
    updateBroadcast,
    dispatchBroadcast,
    scheduleBroadcast,
    cancelBroadcast,
    deleteBroadcast,
    previewAudience,
    uploadMedia,
    listWhatsappTemplates,
    getSettings,
    updateSettings,
    getPendingPopups,
    getMyHistory,
    getBroadcastViewer,
    markBroadcastSeen,
    acknowledgeBroadcast,
};
